// @ts-strict-ignore
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";

import { Tooltip, Pill } from "@aviary/components";
import { useIsDS4Enabled } from "@aviary/hooks/useIsDS4Enabled";
import { l } from "@aviary/locales/i18n";
import type { AviarySize, LayoutStyles } from "@aviary/types";
import { SvgFullscriptIcon, SvgFullscriptIconDarkGreen } from "@shared/assets/logo";

import * as styles from "./OnlyOnFullscriptBadge.styles";

interface Props {
  /**
   * Determines if Pill or icon is rendered
   */
  size?: Extract<AviarySize, "xsmall" | "small" | "large">;
  /**
   * Determines if tooltip is hidden for icon
   *
   * @default false
   */
  isTooltipHidden?: boolean;
  /**
   * Allows you to pass custom styles, use sparingly!
   */
  layoutStyles?: LayoutStyles;
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/molecules/ProductImage/OnlyOnFullscriptBadge
 */
const OnlyOnFullscriptBadge = ({
  size = "small",
  isTooltipHidden = false,
  layoutStyles,
}: Props) => {
  const { t } = useTranslation();
  const isDS4Enabled = useIsDS4Enabled();
  const currentTheme = useTheme();
  const isDarkMode = isDS4Enabled && currentTheme.theme === "dark";

  const isPill = size === "large";

  const renderFullscriptIcon = () => {
    const iconStyles = [!isPill && styles.icon.base, !isPill && styles.icon[size]];
    const iconWrapperStyles = [
      !isPill && styles.iconWrapper.base,
      !isPill && styles.iconWrapper[size],
      !isPill && isTooltipHidden && layoutStyles,
    ];

    if (isDarkMode) {
      return (
        <div css={iconWrapperStyles}>
          <SvgFullscriptIcon css={iconStyles} />
        </div>
      );
    } else {
      return (
        <div css={iconWrapperStyles}>
          <SvgFullscriptIconDarkGreen css={iconStyles} />
        </div>
      );
    }
  };

  if (isPill) {
    return (
      <Pill isColor="success" css={[styles.pill, layoutStyles]}>
        {renderFullscriptIcon()}
        {t(l.aviary.productImage.onlyOnFS)}
      </Pill>
    );
  }

  return (
    <Tooltip
      tooltipContent={t(l.aviary.productImage.onlyOnFS)}
      css={[styles.iconTooltip, layoutStyles]}
      isTooltipHidden={isTooltipHidden}
    >
      {renderFullscriptIcon()}
    </Tooltip>
  );
};

export { OnlyOnFullscriptBadge };
